import { FormikErrors } from 'formik';

import { CallApiWithNotification } from 'src/clients/ApiService';
import UiClient from 'src/clients/UiClients';
import { AuthStateTypes } from 'src/constants/authConsts';
import { PLAN_PAGE } from 'src/constants/pathConsts';
import { AppThunkAction } from 'src/store/reduxTypes';
import {
  AlertMessageModel,
  AlertVariant,
  ChangeModel,
  CLEAR_BANNER_DATA,
  CLEAR_PAGE_CHANGES,
  CLEAR_SNACKBAR_ALERT,
  ClearBannerActionType,
  ClientPreviewSteps,
  CoachmarkTourKey,
  COMPLETE_COACHMARK,
  COMPLETE_PROGRESS,
  CompleteCoachmarkAction,
  CONTROL_LOGIN_MODAL,
  ENABLE_NATIVE_FIND,
  EnableNativeFindValueActionType,
  PAGE_RELOADING,
  PageChangeForm,
  PrimarySideBarMobileType,
  RESET_PROGRESS_VALUE,
  RightSideBarType,
  SAVE_BACK_TO_PORTAL_PAGE,
  SaveBackToPortalPageAction,
  SecondarySideBarType,
  SET_AGREE_PLAN_CANCEL_TERMS,
  SET_BANNER_DATA,
  SET_CAN_RESET_SETTINGS_FORM_PAGE,
  SET_CAN_SUBMIT_FORM_PAGE,
  SET_CLIENT_PREVIEW_STEPS,
  SET_COACHMARK_ACTIVE,
  SET_FORM_PAGE_CHANGED,
  SET_HAS_RECENTLY_VISITED,
  SET_PAGE_CHANGE_ERRORS,
  SET_PAGE_CHANGES,
  SET_PROGRESS_VALUE,
  SET_SECONDARY_SIDEBAR_HIDDEN,
  SET_STUDIO_CLIENT_PREVIEW,
  SetClientPreviewStepsAction,
  SetCoachmarkActiveAction,
  SetStudioClientPreviewAction,
  ShowBannerActionType,
  SNACKBAR_ALERT,
  TOGGLE_AUTO_SIGNIN,
  TOGGLE_HELP_CENTER_MODAL,
  TOGGLE_PRIMARY_SIDEBAR_MOBILE,
  TOGGLE_REFERRAL_PROMO_MODAL,
  TOGGLE_RIGHT_SIDEBAR,
  TOGGLE_SECONDARY_SIDEBAR,
  ToggleHelpCenterModalType,
  ToggleReferralPromoModalType,
  UN_SET_PAGE_CHANGES,
  UPDATE_SEARCH_VALUE,
  UpdateSearchValueActionType,
} from 'src/store/ui/types';
import { PortalConfigUtils } from 'src/utils/PortalUtils';

export const clearAlertMessage = () => ({
  type: CLEAR_SNACKBAR_ALERT,
});

/**
 * This action is used to show snackbar alerts which can
 * be grouped into two categories:
 * Messages alerts: shows simple messages (error/success/info)
 * Progress alerts: shows an async action progress (ex. file upload)
 * @param alertMessage
 * @param isProgress
 */
export const callSnackbar = (
  alertMessage: AlertMessageModel,
  isProgress = false,
) => {
  if (alertMessage && !!alertMessage.successMessage) {
    return {
      type: SNACKBAR_ALERT,
      alertMessage: alertMessage.successMessage,
      alertMessageType: AlertVariant.SUCCESS,
      actionProps: alertMessage.actionProps,
    };
  }
  if (alertMessage && alertMessage.errorMessage) {
    return {
      type: SNACKBAR_ALERT,
      alertMessage: alertMessage.errorMessage,
      actionProps: alertMessage.actionProps,
    };
  }

  if (isProgress) {
    return {
      type: SNACKBAR_ALERT,
      alertMessageType: AlertVariant.PROGRESS,
      alertMessage: alertMessage?.progressMessage,
    };
  }

  const { axiosError } = alertMessage;
  if (axiosError) {
    const { data, status } = axiosError;
    if (data && status) {
      const { message } = data;
      if (message) {
        return { type: SNACKBAR_ALERT, alertMessage: message };
      }
      // List available status
      if (status === 403) {
        return {
          type: SNACKBAR_ALERT,
          alertMessage: `Sorry, you don't have permission to ${
            alertMessage && alertMessage.actionMessage
              ? alertMessage.actionMessage
              : 'do this action'
          }`,
        };
      }
      return {
        type: SNACKBAR_ALERT,
        alertMessage: `Sorry, there was a problem to ${
          alertMessage && alertMessage.actionMessage
            ? alertMessage.actionMessage
            : 'do this action'
        }`,
      };
    }
  }
  return {
    type: SNACKBAR_ALERT,
    alertMessage: 'There was a problem to do this action',
  };
};

/**
 * This action is used to show snackbar alerts which can
 * be grouped into two categories:
 * Messages alerts: shows simple messages (error/success/info)
 * Progress alerts: shows an async action progress (ex. file upload)
 * @param alertMessage
 * @param isProgress
 */
export const alertSnackbar =
  (alertMessage: AlertMessageModel, isProgress = false): AppThunkAction =>
  async (dispatch) => {
    dispatch(clearAlertMessage());
    setTimeout(() => dispatch(callSnackbar(alertMessage, isProgress)), 100);
  };

export const openAuthModal = (authType: AuthStateTypes, nextPath?: string) => ({
  type: CONTROL_LOGIN_MODAL,
  payload: {
    authType,
    nextPath,
  },
});

export const closeAuthModal = () => ({
  type: CONTROL_LOGIN_MODAL,
  payload: {},
});

export const togglePrimarySidebarMobile = (
  params: PrimarySideBarMobileType,
) => ({
  type: TOGGLE_PRIMARY_SIDEBAR_MOBILE,
  payload: params,
});

export const toggleSecondarySidebar = (params: SecondarySideBarType) => ({
  type: TOGGLE_SECONDARY_SIDEBAR,
  payload: params,
});

export const setSecondarySidebarHidden = (hidden: boolean) => ({
  type: SET_SECONDARY_SIDEBAR_HIDDEN,
  payload: hidden,
});

export const toggleRightSidebar = (params: RightSideBarType) => ({
  type: TOGGLE_RIGHT_SIDEBAR,
  payload: params,
});

export const clearPageChanges = () => ({
  type: CLEAR_PAGE_CHANGES,
});

export const setPageReloading = () => ({
  type: PAGE_RELOADING,
});

export const setPageChanges = (changes: ChangeModel[]) => ({
  type: SET_PAGE_CHANGES,
  payload: changes,
});

export const unSetPageChanges = (fields: string[]) => ({
  type: UN_SET_PAGE_CHANGES,
  payload: fields,
});

export const setPageChangeErrors = (errors: FormikErrors<PageChangeForm>) => ({
  type: SET_PAGE_CHANGE_ERRORS,
  payload: errors,
});

export const setAgreePlanCancelTermsAction = (agree: boolean) => ({
  type: SET_AGREE_PLAN_CANCEL_TERMS,
  payload: agree,
});

export const uploadPageChanges =
  (config: any, changedValues: any): AppThunkAction =>
  async (dispatch) => {
    function clear() {
      return { type: CLEAR_PAGE_CHANGES };
    }

    const result = await CallApiWithNotification({
      executeFunction: UiClient.uploadPortalConfig,
      params: {
        config: PortalConfigUtils.MapConfigProperty(changedValues, config),
        source: 'customization',
      },
      successMessage: `Changes have been saved`,
      errorMessage: `These changes could not be saved.`,
      dispatch,
    });

    if (result.status) {
      dispatch(setPageReloading());
      dispatch(clear());
    } else {
      dispatch(clear());
    }
  };

export const setFormPageChanged = (isChanged: boolean) => ({
  type: SET_FORM_PAGE_CHANGED,
  payload: isChanged,
});

export const setCanSubmitFormPage = (canSubmit: boolean) => ({
  type: SET_CAN_SUBMIT_FORM_PAGE,
  payload: canSubmit,
});

export const setCanResetSettingsFormPage = (canReset: boolean) => ({
  type: SET_CAN_RESET_SETTINGS_FORM_PAGE,
  payload: canReset,
});

export const setProgressValue = (fileProgress: number) => ({
  type: SET_PROGRESS_VALUE,
  payload: fileProgress,
});

export const completeProgress = () => ({
  type: COMPLETE_PROGRESS,
});

export const resetProgressValue = () => ({
  type: RESET_PROGRESS_VALUE,
});

export const toggleAutoSignIn =
  (): AppThunkAction => async (dispatch, getState) => {
    const { ui } = getState();
    const toggle = () => ({
      type: TOGGLE_AUTO_SIGNIN,
      payload: !ui.isAutoSignIn,
    });

    dispatch(toggle());
  };

export const updateSearchValueAction = (
  searchValue: string,
): UpdateSearchValueActionType => ({
  type: UPDATE_SEARCH_VALUE,
  payload: searchValue,
});

export const enableNativeFind = (
  enable: boolean,
): EnableNativeFindValueActionType => {
  return {
    type: ENABLE_NATIVE_FIND,
    payload: enable,
  };
};

export const showTrialBannerAction = (
  message: string,
): ShowBannerActionType => ({
  type: SET_BANNER_DATA,
  payload: {
    message,
    buttonOptions: {
      label: 'Select a plan',
      path: PLAN_PAGE.path,
    },
  },
});

export const clearBannerAction = (): ClearBannerActionType => ({
  type: CLEAR_BANNER_DATA,
});

export const toggleHelpCenterModalAction = (
  isOpen: boolean,
): ToggleHelpCenterModalType => ({
  type: TOGGLE_HELP_CENTER_MODAL,
  payload: isOpen,
});

export const toggleReferralPromoModalAction = (
  isOpen: boolean,
): ToggleReferralPromoModalType => ({
  type: TOGGLE_REFERRAL_PROMO_MODAL,
  payload: isOpen,
});

export const saveBackToPortalPage = (
  pagePath: string,
): SaveBackToPortalPageAction => ({
  type: SAVE_BACK_TO_PORTAL_PAGE,
  payload: pagePath,
});

export const setHasRecentlyVisited = (payload: {
  apps: boolean;
  crm: boolean;
}) => ({
  type: SET_HAS_RECENTLY_VISITED,
  payload,
});

export const setCoachmarkActive = (
  key: CoachmarkTourKey,
  active: boolean,
): SetCoachmarkActiveAction => ({
  type: SET_COACHMARK_ACTIVE,
  key,
  payload: active,
});

// Fired when the tour's goal action is taken (e.g. the highlighted "Open portal"
// click): ends the presentation and marks that tour finished so it stops rendering.
export const completeCoachmark = (
  key: CoachmarkTourKey,
): CompleteCoachmarkAction => ({
  type: COMPLETE_COACHMARK,
  key,
});

export const setStudioClientPreview = (
  isPreview: boolean,
  progress = 0,
): SetStudioClientPreviewAction => ({
  type: SET_STUDIO_CLIENT_PREVIEW,
  payload: { isPreview, progress },
});

export const setClientPreviewSteps = (
  steps: ClientPreviewSteps,
): SetClientPreviewStepsAction => ({
  type: SET_CLIENT_PREVIEW_STEPS,
  steps,
});
